import { translate } from '@utils/i18n';
import { formatFileSize } from '@features/Attachments/utils/fileFormatting';
import { UPLOAD_NS } from '../constants';
/**
 * No per-row fields in the drawer, and no type column.
 *
 * The drawer is opened from one document type's header and is bound to it (plan decision 5), so
 * there is nothing for the user to choose per file. Everything else the row needs —
 * `target_public_id` and the uploader columns — is derived server-side in
 * `Api::Internal::Subcontractors::AttachmentUploadsController`, deliberately: accepting any of it
 * from the client would let a crafted request file a document against another inspection.
 */
export const SUBCONTRACTOR_DEFAULT_UPLOAD_ATTRIBUTES = {};
export function toSubcontractorQueueFields() {
    return {};
}
export function buildAddAttachmentsColumns() {
    return [];
}
/** The registry's sentinel for "anything goes"; a type carrying it restricts nothing. */
const ANY_CONTENT_TYPE = '*/*';
/**
 * Turns the document type's `accepted_formats` into the short labels the dropzone note shows —
 * `['application/pdf', 'image/png']` becomes `PDF, PNG`.
 *
 * The subtype is what people recognise, so it is uppercased and the `vnd.*` vendor prefixes and
 * `+xml`-style suffixes that nobody reads are dropped. Anything that is not a `type/subtype` pair
 * is passed through as written rather than mangled — these are tenant-configured strings, and a
 * configured `image/*` should read as `IMAGE/*`, not disappear.
 */
export function formatAcceptedTypes(contentTypes) {
    const labels = contentTypes
        .map((contentType) => {
        const subtype = contentType.split('/')[1];
        if (!subtype)
            return contentType;
        return subtype
            .replace(/^vnd\..*?([^.+]+)$/, '$1')
            .split('+')[0]
            .toUpperCase();
    })
        .filter((label, index, all) => all.indexOf(label) === index);
    return labels.join(', ');
}
/**
 * All `AddAttachmentsBrushaway` copy — the organism takes `labels` and never imports `translate`.
 *
 * The dropzone note names the accepted formats as well as the size cap, because on this surface
 * both are per document TYPE and configured by the tenant. The other four surfaces can hardcode
 * "PDF files only" in their locale; here a type accepting PDF and PNG sits next to one accepting
 * PDF alone, and a drawer that mentioned only the size left the user to discover the format rule
 * by being refused.
 */
export function buildAddAttachmentsLabels(maxSizeBytes, acceptContentTypes = []) {
    const sizeLimitLabel = formatFileSize(maxSizeBytes);
    const NS = UPLOAD_NS;
    const restrictedTypes = acceptContentTypes.filter((contentType) => contentType !== ANY_CONTENT_TYPE);
    const formats = formatAcceptedTypes(restrictedTypes);
    return {
        title: translate('title', { namespace: NS }),
        columnName: translate('column_name', { namespace: NS }),
        columnType: translate('column_type', { namespace: NS }),
        retryActionLabel: translate('retry_action', { namespace: NS }),
        removeActionLabel: (fileName) => translate('remove_action', { namespace: NS, fileName }),
        metaSize: (size) => translate('meta.size', { namespace: NS, size }),
        metaWaiting: (size) => translate('meta.waiting', { namespace: NS, size }),
        metaUploaded: (size) => translate('meta.uploaded', { namespace: NS, size }),
        metaFinalizing: (size) => translate('meta.finalizing', { namespace: NS, size }),
        footerTotals: (count, size) => translate('footer_totals', { namespace: NS, count, size }),
        cancelActionLabel: translate('cancel_action', { namespace: NS }),
        metaFailed: (size) => translate('meta.failed', { namespace: NS, size }),
        addButtonLabel: (count) => translate('add_button', { namespace: NS, count }),
        uploadingSummary: (done, total, pct) => translate('uploading_summary', { namespace: NS, done, total, pct }),
        uploadedSummary: (done, total, failed) => translate('uploaded_summary', { namespace: NS, done, total, failed }),
        cancelUploadLabel: translate('cancel_upload', { namespace: NS }),
        retryFailedLabel: translate('retry_failed', { namespace: NS }),
        rejectedAlertTitle: (count) => translate('rejected_alert.title', { namespace: NS, count }),
        rejectedAlertMessage: (fileName, size) => translate('rejected_alert.message', { namespace: NS, fileName, size, limit: sizeLimitLabel }),
        rejectedAlertTypeMessage: (fileName) => translate('rejected_alert.type_message', { namespace: NS, fileName }),
        dropzoneNote: formats
            ? translate('dropzone_note_with_formats', { namespace: NS, limit: sizeLimitLabel, formats })
            : translate('dropzone_note', { namespace: NS, limit: sizeLimitLabel }),
        removeConfirmTitle: translate('remove_confirm.title', { namespace: NS }),
        removeConfirmDescription: (fileName) => translate('remove_confirm.description', { namespace: NS, fileName }),
        removeConfirmConfirmLabel: translate('remove_confirm.confirm_label', { namespace: NS }),
        removeConfirmCancelLabel: translate('remove_confirm.cancel_label', { namespace: NS }),
        discardConfirmTitle: translate('discard_confirm.title', { namespace: NS }),
        discardConfirmDescription: translate('discard_confirm.description', { namespace: NS }),
        discardConfirmConfirmLabel: translate('discard_confirm.confirm_label', { namespace: NS }),
        discardConfirmCancelLabel: translate('discard_confirm.cancel_label', { namespace: NS }),
        closeLabel: translate('close_label', { namespace: NS })
    };
}
